/**
 * Propagates a fresh read_at value from a single proposal-detail fetch into
 * every matching list cache entry for the org. Bridges the gap between the
 * detail query (which stamps read_at on first retrieve) and the list query
 * (which would otherwise hold stale read_at=null for up to staleTime).
 *
 * Idempotency: when the existing entry's read_at already matches the incoming
 * value, the cache is NOT rewritten — this prevents render thrash and avoids
 * feedback loops with the sidebar badge's pulse trigger.
 */

import type { QueryClient } from '@tanstack/react-query';

import { logger } from '@/lib/logger';

import type { ProposalsListResponse } from '../core/types';
import { proposalQueryKeys } from '../query/keys';

export function propagateProposalReadAt(
  queryClient: QueryClient,
  orgId: number,
  proposalId: number,
  readAt: string | null
): void {
  // No propagation needed when the detail fetch has no read_at to share.
  if (readAt === null) {
    return;
  }

  const cache = queryClient.getQueryCache();
  const matches = cache.findAll({ queryKey: proposalQueryKeys.lists(orgId) });

  let matchedCaches = 0;
  let updatedEntries = 0;

  for (const query of matches) {
    const list = query.state.data as ProposalsListResponse | undefined;
    // Skip undefined cache entries — same skip-condition as applyProposalCreated
    // (avoids synthesizing a partial response with wrong count / filter_tag_counts).
    // Also skip entries without a flat `results` array: the history views'
    // infinite caches live under the same lists prefix but hold InfiniteData
    // ({ pages }) — invalidation keeps those fresh; surgical read_at
    // propagation only applies to flat list responses (history rows carry no
    // unread affordance anyway).
    if (!list || !Array.isArray(list.results)) {
      continue;
    }
    matchedCaches += 1;

    const idx = list.results.findIndex(p => p.id === proposalId);
    if (idx < 0) {
      // Proposal not in this cache — nothing to update here. The next list
      // fetch will see the fresh read_at.
      continue;
    }
    if (list.results[idx].read_at === readAt) {
      // Idempotency guard — already up to date. Skipping the setQueryData call
      // avoids fresh object references that would otherwise re-render every
      // subscribed component (badge, list panel, dashboard).
      continue;
    }

    const nextResults = [...list.results];
    nextResults[idx] = { ...nextResults[idx], read_at: readAt };
    queryClient.setQueryData<ProposalsListResponse>(query.queryKey, {
      ...list,
      results: nextResults,
    });
    updatedEntries += 1;
  }

  logger.debug('[propagateProposalReadAt] cache update', {
    orgId,
    proposalId,
    matchedCaches,
    updatedEntries,
  });
}
